/**
 * PreferenceStore — Singleton pub/sub and snapshot utility.
 *
 * Provides key-specific subscriptions so that React components can
 * selectively re-render when monitored preferences change.
 *
 * Write interception is handled automatically by PreferenceContext.Provider,
 * which wraps the service and calls notify() after each write.
 */

import type {
  PreferenceLevelValue,
  PreferenceService,
  PreferenceSnapshot,
  PreferenceSubscriber,
  PreferenceValue,
} from './types';

const subscribers = new Map<string, Set<PreferenceSubscriber>>();

/**
 * Subscribe to changes for a specific preference key.
 * @param {string} key - The preference name to watch.
 * @param {function} callback - Called when the preference value changes.
 * @returns {function} Unsubscribe function.
 */
const subscribe = (key: string, callback: PreferenceSubscriber) => {
  if (!subscribers.has(key)) {
    subscribers.set(key, new Set());
  }
  subscribers.get(key).add(callback);

  return () => {
    const subs = subscribers.get(key);
    if (subs) {
      subs.delete(callback);
      if (subs.size === 0) {
        subscribers.delete(key);
      }
    }
  };
};

/**
 * Notify all subscribers of a specific preference key.
 * @param {string} key - The preference name that changed.
 * @returns {void}
 */
const notify = (key: string): void => {
  const subs = subscribers.get(key);
  if (subs) {
    subs.forEach((cb) => {
      try {
        cb();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error in subscriber callback for key "${key}":`, error);
      }
    });
  }
};

/**
 * Read the current .Data value for a preference key at a specific level.
 * @param {object} preferenceService - The PreferenceService instance.
 * @param {string} key - Preference name.
 * @param {string} [level='user'] - 'user' or 'account'.
 * @returns {*} The preference Data value, or undefined.
 */
const getValue = <TValue = PreferenceValue>(
  preferenceService: PreferenceService,
  key: string,
  level: PreferenceLevelValue = 'user',
): TValue | undefined => {
  let pref;
  if (level === 'account' && typeof preferenceService.findByNameAtAccountLevel === 'function') {
    pref = preferenceService.findByNameAtAccountLevel(key);
  } else if (typeof preferenceService.findByNameAtUserLevel === 'function') {
    pref = preferenceService.findByNameAtUserLevel(key);
  } else {
    pref = preferenceService.findByName?.(key);
  }
  return pref ? pref.Data as TValue : undefined;
};

/**
 * Build a snapshot object of { key: Data } for the given keys.
 * @param {object} preferenceService - The PreferenceService instance.
 * @param {string[]} keys - Preference names to read.
 * @param {string} [level='user'] - 'user' or 'account'.
 * @returns {Object<string, *>} Snapshot of current values.
 */
const getSnapshot = <TValue = PreferenceValue>(
  preferenceService: PreferenceService,
  keys: string[],
  level: PreferenceLevelValue = 'user',
): PreferenceSnapshot<TValue> => {
  const snapshot: PreferenceSnapshot<TValue> = {};
  for (let i = 0; i < keys.length; i += 1) {
    snapshot[keys[i]] = getValue(preferenceService, keys[i], level);
  }
  return snapshot;
};

export const PreferenceStore = {
  subscribe,
  notify,
  getValue,
  getSnapshot,
};
